import React from 'react';

import { Box, Center, Loader, SimpleGrid } from '@mantine/core';
import dayjs from 'dayjs';
import useWorkoutsCalendar from 'hooks/workout/useWorkoutsCalendar';
import type { WorkoutCalendar } from 'types';
import { getWeekRange } from 'utils/date-functions';

import { FilterTerms, ProgramCalendarView } from './types';
import WeekDayColumn from './WeekView/WeekDayColumn';
import WeekNavigationHeader from './WeekView/WeekNavigationHeader';

interface ProgramCalendarWeekViewProps {
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  setView: React.Dispatch<React.SetStateAction<ProgramCalendarView>>;
  searchFilters: FilterTerms;
}

const ProgramCalendarWeekView = ({
  date,
  setDate,
  setView,
  searchFilters,
}: ProgramCalendarWeekViewProps): JSX.Element => {
  const { start, end } = getWeekRange(date);
  const { data, isLoading, isError } = useWorkoutsCalendar({ startDate: start, endDate: end });

  const today = dayjs().format('YYYY-MM-DD');
  const days = Array.from({ length: 7 }, (_, i) => dayjs(start).add(i, 'day').format('YYYY-MM-DD'));

  const byDate = new Map<string, WorkoutCalendar>();
  ((data?.data as WorkoutCalendar[] | undefined) ?? []).forEach(d => byDate.set(d.date, d));

  const rowsFor = (day: string): WorkoutCalendar['kiloClassTypes'] => {
    const all = byDate.get(day)?.kiloClassTypes ?? [];
    return searchFilters.kiloClassTypes.length > 0
      ? all.filter(k => searchFilters.kiloClassTypes.includes(k.kiloClassTypeId))
      : all;
  };

  const handleHeaderClick = (day: string): void => {
    setDate(day);
    setView('agenda');
  };

  return (
    <Box>
      <WeekNavigationHeader
        view='week'
        setView={setView}
        weekStart={start}
        weekEnd={end}
        onPrev={() => setDate(dayjs(date).subtract(7, 'day').format('YYYY-MM-DD'))}
        onNext={() => setDate(dayjs(date).add(7, 'day').format('YYYY-MM-DD'))}
        onToday={() => setDate(dayjs().format('YYYY-MM-DD'))}
      />
      {isError ? (
        <>Something went wrong</>
      ) : isLoading ? (
        <Center mih={300}>
          <Loader />
        </Center>
      ) : (
        <SimpleGrid cols={7} spacing='xs' p='sm' data-cy='weekViewGrid'>
          {days.map(day => (
            <WeekDayColumn
              key={day}
              date={day}
              classTypes={rowsFor(day)}
              isToday={day === today}
              onHeaderClick={handleHeaderClick}
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default ProgramCalendarWeekView;
